import { useState, useCallback } from "react";
import { toast } from "sonner";
import { api, errMsg, CMD_LABEL } from "@/lib/api";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

function report(summary) {
  const sim = summary.result?.simulated ? " [SIMULADO]" : "";
  const msg = `${CMD_LABEL[summary.status] || summary.status}${sim}`;
  const desc = summary.result?.error || summary.meaning;
  if (summary.status === "confirmada") toast.success(`${summary.tool}: ${msg}`, { description: desc });
  else if (summary.status === "fallida" || summary.status === "incierta") toast.error(`${summary.tool}: ${msg}`, { description: desc });
  else toast(`${summary.tool}: ${msg}`, { description: desc });
}

/** Executes a typed tool through the backend executor; shows a confirmation dialog for risky tools. */
export function useToolRunner() {
  const [pending, setPending] = useState(null);

  const run = useCallback(async (tool, args = {}) => {
    try {
      const { data } = await api.post("/commands/execute", { tool, args });
      if (data.summary.awaiting_confirmation) setPending(data.summary);
      else report(data.summary);
      return data.summary;
    } catch (e) {
      toast.error(`${tool}: no enviada`, { description: errMsg(e) });
      return null;
    }
  }, []);

  const confirm = async () => {
    const p = pending;
    setPending(null);
    try {
      const { data } = await api.post(`/commands/${p.command_id}/confirm`);
      report(data.summary);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const cancel = async () => {
    const p = pending;
    setPending(null);
    await api.post(`/commands/${p.command_id}/cancel`).catch(() => {});
    toast("Orden cancelada");
  };

  const dialog = (
    <AlertDialog open={!!pending} onOpenChange={(o) => !o && pending && cancel()}>
      <AlertDialogContent className="bg-slate-950 border-cyan-500/40" data-testid="confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-hud text-cyan-300">Confirmar acción</AlertDialogTitle>
          <AlertDialogDescription className="text-slate-300">
            Jarvis necesita su autorización para ejecutar <b className="text-cyan-200">{pending?.tool}</b>. Esta acción es difícil de revertir.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="confirm-dialog-cancel" onClick={cancel}>Cancelar</AlertDialogCancel>
          <AlertDialogAction data-testid="confirm-dialog-accept" className="bg-cyan-500 text-slate-950 hover:bg-cyan-400" onClick={confirm}>Autorizar</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  return { run, dialog, askConfirm: setPending };
}
